import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { MsalService, MSAL_INSTANCE } from '@azure/msal-angular';
import {
  Configuration,
  IPublicClientApplication,
  PublicClientApplication,
} from '@azure/msal-browser';
import { Subject, filter, take, takeUntil, timeout } from 'rxjs';
import { AuthenticationService } from '@auth/authentication.service';
import { ActiveAuthenticationStateService } from '@auth/active-authentication.state-service';
import { ActiveSecurityContextStateService } from '@auth/active-security-context.state-service';
import { LoggingService } from '@logging/logging.service';
import { LogType } from '@logging/log';
import { environment } from '@env/environment';
import { authenticationRoutePaths } from '../authentication-route-paths';
import { InFlightSpinnerComponent } from '@design-system/loaders/spinners/in-flight-spinner/in-flight-spinner.component';
import { H2Component } from '@design-system/typography/h2/h2.component';
import { BodyMediumComponent } from '@design-system/typography/body-md/body-medium.component';
import { TestIdDirective } from '@design-system/core/test-id.directive';

export function SSOLandingMSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication({
    auth: {
      clientId: '',
      authority: '',
      redirectUri: '',
      navigateToLoginRequestUrl: true,
    },
    cache: {
      cacheLocation: 'sessionStorage',
      storeAuthStateInCookie: false,
    },
  });
}

/**
 * Handles the IdP redirect after SSO: exchanges the auth code for tokens, then completes
 * sign-in via the existing token sign-in flow and redirects into the app.
 */
@Component({
  selector: 'app-sso-landing',
  standalone: true,
  imports: [
    CommonModule,
    InFlightSpinnerComponent,
    H2Component,
    BodyMediumComponent,
    TestIdDirective,
  ],
  providers: [
    { provide: MSAL_INSTANCE, useFactory: SSOLandingMSALInstanceFactory },
    MsalService,
  ],
  templateUrl: './sso-landing.component.html',
  styleUrl: './sso-landing.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SsoLandingComponent implements OnInit, OnDestroy {
  status: 'processing' | 'success' | 'error' = 'processing';
  private readonly destroy$ = new Subject<void>();

  constructor(
    private msalService: MsalService,
    private authenticationService: AuthenticationService,
    private logging: LoggingService,
    private activeAuthenticationStateService: ActiveAuthenticationStateService,
    private activeSecurityContextStateService: ActiveSecurityContextStateService,
    private router: Router
  ) {}

  async ngOnInit(): Promise<void> {
    this.logging.info('SSO landing: processing redirect');

    try {
      if (!environment?.msalSettings) {
        this.logError('MSAL settings not found in app configuration');
        this.navigateToSignIn();
        return;
      }

      const msalInstance = new PublicClientApplication(this.getMsalConfig());
      await msalInstance.initialize();
      Object.assign(this.msalService.instance, msalInstance);

      const result = await this.msalService.instance.handleRedirectPromise();

      if (result?.idToken) {
        this.setMsalAccountIfPresent(result.account);
        this.completeSignIn(result.idToken);
      } else {
        this.logError(
          'No token in redirect response (code exchange failed or no redirect)'
        );
        this.navigateToSignIn();
      }
    } catch (err) {
      this.logError(
        `Error processing redirect: ${this.errorMessage(err)}`
      );
      this.navigateToSignIn();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private getMsalConfig(): Configuration {
    return {
      auth: {
        clientId: environment.msalSettings.clientId,
        authority: environment.msalSettings.authority,
        redirectUri: environment.msalSettings.redirect,
        navigateToLoginRequestUrl: false,
      },
      cache: {
        cacheLocation: 'sessionStorage',
        storeAuthStateInCookie: false,
      },
    };
  }

  private setMsalAccountIfPresent(account: unknown): void {
    if (!account) return;
    try {
      this.msalService.instance.setActiveAccount(account as any);
    } catch {
      this.logging.info(
        'SSO landing: could not set MSAL active account (continuing)'
      );
    }
  }

  private completeSignIn(idToken: string): void {
    this.authenticationService
      .userSignInByToken$(idToken)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (authState) => {
          if (authState.success) {
            this.status = 'success';
            this.redirectWhenIdentityReady();
          } else {
            this.logError('Token sign-in returned success=false');
            this.navigateToSignIn();
          }
        },
        error: (error: unknown) => {
          this.logError(`Token sign-in failed: ${this.errorMessage(error)}`);
          this.navigateToSignIn();
        },
      });
  }

  private redirectWhenIdentityReady(): void {
    const redirect = () =>
      this.activeSecurityContextStateService.redirectToBestRoute();
    this.activeAuthenticationStateService.activeIdentity$
      .pipe(
        filter((identity) => !!identity),
        take(1),
        timeout(15000),
        takeUntil(this.destroy$)
      )
      .subscribe({ next: redirect, error: redirect });
  }

  private navigateToSignIn(): void {
    this.status = 'error';
    this.router.navigateByUrl(authenticationRoutePaths.signIn).then();
  }

  private logError(message: string): void {
    this.logging.error(`SSO landing: ${message}`, { type: LogType.Error });
  }

  private errorMessage(err: unknown): string {
    return err instanceof Error ? err.message : String(err);
  }
}
